import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import RobustSelect from '../../components/RobustSelect';
import { SelectItem } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { Plus, X, ArrowLeft, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import LaTeXRenderer from '../../components/LaTeXRenderer';

function CreateQuestion() {
  const navigate = useNavigate();
  const location = useLocation();
  const [banks, setBanks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    bank_id: location.state?.bankId || 'none',
    question_type: 'mcq_single',
    question_text: '',
    choices: ['', '', '', ''],
    correct_answer: '',
    explanation: '',
    media_url: '',
    media_type: '',
    difficulty: 'medium',
    tags: '',
    points: 1,
  });

  const fetchBanks = async () => {
    try {
      const response = await api.get('/question-banks');
      setBanks(response.data);
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/upload/image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setFormData({
        ...formData,
        media_url: response.data.url,
        media_type: 'image',
      });

      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setFormData({
      ...formData,
      media_url: '',
      media_type: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        choices: formData.choices.filter(c => c.trim()),
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
      };
      await api.post('/questions', data);
      toast.success('Question created successfully');
      navigate(`/admin/question-banks/${formData.bank_id}`);
    } catch (error) {
      console.error('Error creating question:', error);
    }
  };

  const addChoice = () => {
    setFormData({ ...formData, choices: [...formData.choices, ''] });
  };

  const updateChoice = (index, value) => {
    const newChoices = [...formData.choices];
    newChoices[index] = value;
    setFormData({ ...formData, choices: newChoices });
  };

  const removeChoice = (index) => {
    const newChoices = formData.choices.filter((_, i) => i !== index);
    setFormData({ ...formData, choices: newChoices });
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <h1 className="text-3xl font-bold text-gray-900 mb-8" data-testid="create-question-title">Create Question</h1>

        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="bank_id">Question Bank *</Label>
                <RobustSelect
                  value={formData.bank_id}
                  onValueChange={(value) => setFormData({ ...formData, bank_id: value })}
                  placeholder="Select a bank"
                  testId="bank-select"
                >
                  <SelectItem value="none" disabled>Select a question bank</SelectItem>
                  {banks.map((bank) => (
                    <SelectItem key={bank.id} value={bank.id}>
                      {bank.title}
                    </SelectItem>
                  ))}
                </RobustSelect>
              </div>

              <div className="space-y-2">
                <Label htmlFor="question_type">Question Type *</Label>
                <RobustSelect
                  value={formData.question_type}
                  onValueChange={(value) => setFormData({ ...formData, question_type: value })}
                  testId="type-select"
                >
                  <SelectItem value="mcq_single">MCQ (Single Correct)</SelectItem>
                  <SelectItem value="mcq_multiple">MCQ (Multiple Correct)</SelectItem>
                  <SelectItem value="true_false">True/False</SelectItem>
                  <SelectItem value="fill_blank">Fill in the Blank</SelectItem>
                  <SelectItem value="short_answer">Short Answer</SelectItem>
                </RobustSelect>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="question_text">Question Text *</Label>
              <Textarea
                id="question_text"
                data-testid="question-text-input"
                value={formData.question_text}
                onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                rows={4}
                required
              />
            </div>

            {['mcq_single', 'mcq_multiple'].includes(formData.question_type) && (
              <div className="space-y-3">
                <Label>Choices *</Label>
                {formData.choices.map((choice, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={choice}
                      onChange={(e) => updateChoice(index, e.target.value)}
                      placeholder={`Choice ${index + 1}`}
                      data-testid={`choice-input-${index}`}
                    />
                    {formData.choices.length > 2 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeChoice(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addChoice} data-testid="add-choice-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Choice
                </Button>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="correct_answer">Correct Answer *</Label>
              <Input
                id="correct_answer"
                data-testid="correct-answer-input"
                value={formData.correct_answer}
                onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
                placeholder="Enter correct answer or choice index (e.g., 0, 1, 2)"
                required
              />
            </div>

            {/* Image Upload Section */}
            <div className="space-y-3 p-4 border-2 border-dashed rounded-lg bg-gray-50">
              <Label className="text-base font-semibold flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Question Image (Optional)
              </Label>
              
              {!formData.media_url ? (
                <div className="space-y-3">
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="media-upload-input"
                  />
                  <label htmlFor="image-upload">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      disabled={uploading}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById('image-upload').click();
                      }}
                    >
                      {uploading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Image
                        </>
                      )}
                    </Button>
                  </label>
                  <p className="text-xs text-gray-500 text-center">
                    Supported formats: JPEG, PNG, GIF, WebP (Max 5MB)
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative inline-block">
                    <img
                      src={formData.media_url}
                      alt="Question preview"
                      className="max-w-full max-h-64 rounded-lg border-2 border-gray-300 shadow-sm"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={removeImage}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-green-600 font-medium">
                    ✓ Image uploaded successfully
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="explanation">Explanation</Label>
              <Textarea
                id="explanation"
                data-testid="explanation-input"
                value={formData.explanation}
                onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="difficulty">Difficulty</Label>
                <RobustSelect
                  value={formData.difficulty}
                  onValueChange={(value) => setFormData({ ...formData, difficulty: value })}
                  testId="difficulty-select"
                >
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </RobustSelect>
              </div>

              <div className="space-y-2">
                <Label htmlFor="points">Points</Label>
                <Input
                  id="points"
                  data-testid="points-input"
                  type="number"
                  min="1"
                  value={formData.points}
                  onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tags">Tags (comma-separated)</Label>
                <Input
                  id="tags"
                  data-testid="tags-input"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="math, algebra"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="submit-question-button">
                Create Question
              </Button>
            </div>
          </form>
        </Card>

        {/* LaTeX Preview */}
        {formData.question_text && (
          <Card className="p-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">LaTeX Preview</h3>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600 mb-2">Question:</p>
              <div className="text-base">
                <LaTeXRenderer text={formData.question_text} />
              </div>
              
              {formData.choices.length > 0 && formData.choices.some(c => c.trim()) && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-2">Answer Choices:</p>
                  <div className="space-y-2">
                    {formData.choices.map((choice, idx) => choice.trim() && (
                      <div key={idx} className="flex items-start gap-2 p-2 bg-white rounded border">
                        <span className="font-medium text-gray-600 min-w-[24px]">{String.fromCharCode(65 + idx)}.</span>
                        <div className="flex-1">
                          <LaTeXRenderer text={choice} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-xs text-blue-800 font-medium mb-2">Auto-Detected Math Patterns:</p>
                <div className="text-xs text-blue-700 grid grid-cols-2 gap-x-4 gap-y-1">
                  <span>• Fractions: 1/2, (a+b)/(c+d), frac(a,b)</span>
                  <span>• Roots: sqrt(x), √x, cbrt(x), ∛x</span>
                  <span>• Exponents: x^2, 10^8, x^{-1}</span>
                  <span>• Subscripts: x_1, H_2O</span>
                  <span>• Greek: alpha, beta, theta, pi, omega</span>
                  <span>• Trig: sin(x), cos(x), tan^2(x)</span>
                  <span>• Logs: ln(x), log(x), log_2(x)</span>
                  <span>• Limits: lim(x->0, f(x))</span>
                  <span>• Sums: sum(i=1, n, expr)</span>
                  <span>• Integrals: int(a, b, f(x)dx)</span>
                  <span>• Matrices: matrix[[a,b],[c,d]]</span>
                  <span>• Conjugates: conj(z), z*</span>
                  <span>• Vectors: vec(A), ->F</span>
                  <span>• Symbols: ×, ÷, ±, ≈, ≤, ≥, ≠, ∞</span>
                </div>
                <p className="text-xs text-blue-800 mt-2">
                  <strong>Explicit LaTeX:</strong> Use $formula$ for inline, $$formula$$ for display
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}

export default CreateQuestion;